// =============================================================================
// Settings → Delivery tab
// =============================================================================
// Three blocks: weekly route schedule, daily capacities + lead time, and the
// city → region hint table for the customer-form auto-suggest.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Region } from '@kalachand/shared';
import { apiDelete, apiGet, apiPatch, apiPost, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Input } from '../../../components/Input';
import { Button } from '../../../components/Button';
import { StatusPill } from '../../../components/StatusPill';
import { cn } from '../../../lib/cn';

const DAYS_OF_WEEK: {
  key: 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
  label: string;
}[] = [
  { key: 'MON', label: 'Monday' },
  { key: 'TUE', label: 'Tuesday' },
  { key: 'WED', label: 'Wednesday' },
  { key: 'THU', label: 'Thursday' },
  { key: 'FRI', label: 'Friday' },
  { key: 'SAT', label: 'Saturday' },
  { key: 'SUN', label: 'Sunday' },
];

const ALL_REGIONS: { value: Region; label: string }[] = [
  { value: 'center', label: 'Center' },
  { value: 'north', label: 'North' },
  { value: 'east', label: 'East' },
  { value: 'south', label: 'South' },
  { value: 'west', label: 'West' },
];

interface SchedulingConfigRow {
  id: string;
  regionsByDayOfWeek: Record<string, Region[]>;
  productionPerDay: number;
  deliveriesPerDay: number;
  productionLeadDays: number;
  updatedAt: string;
}

interface RegionHintRow {
  id: string;
  city: string;
  region: Region;
  displayName: string | null;
  createdAt: string;
}

export function DeliveryTab(): JSX.Element {
  const qc = useQueryClient();
  const config = useQuery({
    queryKey: ['scheduling-config'],
    queryFn: () => apiGet<SchedulingConfigRow>('/scheduling/config'),
  });
  const hints = useQuery({
    queryKey: ['region-hints'],
    queryFn: () => apiGet<RegionHintRow[]>('/scheduling/region-hints'),
  });

  if (config.isLoading || !config.data) {
    return <div className="text-center text-ink-3 py-12">Loading…</div>;
  }
  return (
    <div className="space-y-6">
      <RouteScheduleCard
        config={config.data}
        onSaved={() => qc.invalidateQueries({ queryKey: ['scheduling-config'] })}
      />
      <CapacityCard
        config={config.data}
        onSaved={() => qc.invalidateQueries({ queryKey: ['scheduling-config'] })}
      />
      <RegionHintsCard
        hints={hints.data ?? []}
        loading={hints.isLoading}
        onChanged={() => qc.invalidateQueries({ queryKey: ['region-hints'] })}
      />
    </div>
  );
}

function RouteScheduleCard({
  config,
  onSaved,
}: {
  config: SchedulingConfigRow;
  onSaved: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Record<string, Region[]>>(config.regionsByDayOfWeek);
  const submit = useMutation({
    mutationFn: () => apiPatch('/scheduling/config', { regionsByDayOfWeek: draft }),
    onSuccess: onSaved,
  });
  const toggle = (day: string, region: Region): void => {
    setDraft((prev) => {
      const current = prev[day] ?? [];
      return {
        ...prev,
        [day]: current.includes(region)
          ? current.filter((r) => r !== region)
          : [...current, region],
      };
    });
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(config.regionsByDayOfWeek);

  return (
    <Card
      title="Route schedule"
      subtitle="Which regions does the truck visit on each day? Center can ride along every active day."
      action={
        dirty ? (
          <Button size="sm" loading={submit.isPending} onClick={() => submit.mutate()}>
            Save changes
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-2">
        {DAYS_OF_WEEK.map((d) => {
          const regions = draft[d.key] ?? [];
          return (
            <div
              key={d.key}
              className="flex items-start gap-3 py-2 border-b border-rule-2 last:border-b-0"
            >
              <div className="w-28 text-sm font-medium text-ink pt-1.5">{d.label}</div>
              <div className="flex flex-wrap gap-1.5 flex-1">
                {ALL_REGIONS.map((r) => {
                  const active = regions.includes(r.value);
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => toggle(d.key, r.value)}
                      className={cn(
                        'px-2.5 py-1 rounded text-xs uppercase tracking-wide border transition',
                        active
                          ? 'bg-accent text-white border-accent'
                          : 'bg-paper-2 text-ink-3 border-rule hover:border-accent/40',
                      )}
                    >
                      {r.label}
                    </button>
                  );
                })}
                {regions.length === 0 && (
                  <span className="text-xs text-ink-3 italic pt-1">No deliveries this day</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {submit.isError && (
        <div className="text-xs text-negative mt-3">{getApiError(submit.error).message}</div>
      )}
    </Card>
  );
}

function CapacityCard({
  config,
  onSaved,
}: {
  config: SchedulingConfigRow;
  onSaved: () => void;
}): JSX.Element {
  const [productionPerDay, setProductionPerDay] = useState(String(config.productionPerDay));
  const [deliveriesPerDay, setDeliveriesPerDay] = useState(String(config.deliveriesPerDay));
  const [productionLeadDays, setProductionLeadDays] = useState(String(config.productionLeadDays));
  const submit = useMutation({
    mutationFn: () =>
      apiPatch('/scheduling/config', {
        productionPerDay: Number(productionPerDay),
        deliveriesPerDay: Number(deliveriesPerDay),
        productionLeadDays: Number(productionLeadDays),
      }),
    onSuccess: onSaved,
  });
  const dirty =
    Number(productionPerDay) !== config.productionPerDay ||
    Number(deliveriesPerDay) !== config.deliveriesPerDay ||
    Number(productionLeadDays) !== config.productionLeadDays;

  return (
    <Card
      title="Capacities & lead time"
      subtitle="Daily ceilings for delivery + production. Lead time = days the factory needs before delivery."
      action={
        dirty ? (
          <Button size="sm" loading={submit.isPending} onClick={() => submit.mutate()}>
            Save changes
          </Button>
        ) : undefined
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Input
          label="Deliveries per day"
          type="number"
          min="1"
          max="1000"
          value={deliveriesPerDay}
          onChange={(e) => setDeliveriesPerDay(e.target.value)}
          hint="Hard ceiling — the picker blocks bookings past this."
        />
        <Input
          label="Mattresses produced per day"
          type="number"
          min="1"
          max="1000"
          value={productionPerDay}
          onChange={(e) => setProductionPerDay(e.target.value)}
          hint="Soft — overflow spreads to earlier days."
        />
        <Input
          label="Production lead days"
          type="number"
          min="0"
          max="60"
          value={productionLeadDays}
          onChange={(e) => setProductionLeadDays(e.target.value)}
          hint="Default across categories. Categories can override."
        />
      </div>
      {submit.isError && (
        <div className="text-xs text-negative mt-3">{getApiError(submit.error).message}</div>
      )}
    </Card>
  );
}

function RegionHintsCard({
  hints,
  loading,
  onChanged,
}: {
  hints: RegionHintRow[];
  loading: boolean;
  onChanged: () => void;
}): JSX.Element {
  const [filter, setFilter] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newRegion, setNewRegion] = useState<Region>('center');
  const add = useMutation({
    mutationFn: () => apiPost('/scheduling/region-hints', { city: newCity, region: newRegion }),
    onSuccess: () => {
      setNewCity('');
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/scheduling/region-hints/${id}`),
    onSuccess: onChanged,
  });

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return hints;
    return hints.filter(
      (h) =>
        (h.displayName?.toLowerCase().includes(q) ?? false) ||
        h.city.toLowerCase().includes(q) ||
        h.region.toLowerCase().includes(q),
    );
  }, [hints, filter]);

  return (
    <Card
      title="Region hints"
      subtitle={`${hints.length} city → region mappings. Used to auto-suggest the region when sales girl types the customer's city.`}
      bodyClassName="p-0"
    >
      <div className="px-6 py-3 border-b border-rule grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto_auto] gap-2 items-end">
        <Input
          label="Filter"
          placeholder="Search…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Input
          label="New city"
          placeholder="e.g. Goodlands Phase 2"
          value={newCity}
          onChange={(e) => setNewCity(e.target.value)}
        />
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Region</span>
          <select
            value={newRegion}
            onChange={(e) => setNewRegion(e.target.value as Region)}
            className="kc-input"
          >
            {ALL_REGIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <Button
          size="sm"
          disabled={!newCity.trim()}
          loading={add.isPending}
          onClick={() => add.mutate()}
        >
          + Add
        </Button>
      </div>
      {add.isError && (
        <div className="text-xs text-negative px-6 py-2 border-b border-rule">
          {getApiError(add.error).message}
        </div>
      )}
      {loading ? (
        <div className="text-center text-ink-3 px-6 py-8">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-ink-3 px-6 py-8">No hints match.</div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule sticky top-0">
                <th className="px-6 py-2 font-medium">City</th>
                <th className="px-6 py-2 font-medium">Region</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => (
                <tr key={h.id} className="border-b border-rule-2 hover:bg-paper-2">
                  <td className="px-6 py-2 text-ink">{h.displayName ?? h.city}</td>
                  <td className="px-6 py-2">
                    <StatusPill tone="accent">{h.region.replace('_', '-')}</StatusPill>
                  </td>
                  <td className="px-6 py-2 text-right">
                    <Button
                      type="button"
                      variant="tertiary-danger"
                      size="xs"
                      onClick={() => remove.mutate(h.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
